import { useMemo, useState } from 'react';
import { StoryBadge } from '@/components/stories/StoryBadge';
import { StoryViewer } from '@/components/stories/StoryViewer';
import { getBotAvatarSrc } from '@/components/bots/getBotAvatarSrc';
import { useBots } from '@/hooks/use-bots';
import { useStoryRail } from '@/hooks/use-story-rail';
import { useSession } from '@/hooks/useSessionHook';
import type { StoryRailItem } from '@/types/story';

function getStoryFallback(label: string): string {
  return label.slice(0, 2).toUpperCase();
}

function getBorderClass(story: StoryRailItem): string {
  if (story.isOwn) {
    return 'border-[color:var(--jr-accent)]';
  }

  if (story.viewed) {
    return 'border-[color:var(--jr-border)]';
  }

  return story.authorType === 'bot'
    ? 'border-[color:var(--jr-accent)]'
    : 'border-[color:var(--jr-accent-strong)]';
}

export function StoryRail() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { stories, isLoading, error, refetch } = useStoryRail({
    userId,
    enabled: !!userId,
  });
  const { bots, isLoading: botsLoading } = useBots();
  const [viewerStoryId, setViewerStoryId] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const botStories = useMemo(
    () => stories.filter((story) => story.authorType === 'bot' && story.botId),
    [stories]
  );
  const displayStories = useMemo(() => {
    const latestStoryByBotId = new Map<string, StoryRailItem>();

    for (const story of botStories) {
      if (story.botId && !latestStoryByBotId.has(story.botId)) {
        latestStoryByBotId.set(story.botId, story);
      }
    }

    const syntheticStories = bots.map((bot) => {
      const existingStory = latestStoryByBotId.get(bot.id);
      if (existingStory) {
        return existingStory;
      }

      const avatarUrl = getBotAvatarSrc({
        id: bot.id,
        name: bot.name,
        avatar_url: bot.avatar_url,
      }) || '/defaultAvatar.png';

      return {
        id: `bot-story-${bot.id}`,
        authorType: 'bot',
        authorId: bot.id,
        authorName: bot.name,
        authorAvatarUrl: avatarUrl,
        mediaType: 'image',
        mediaUrl: avatarUrl,
        thumbnailUrl: avatarUrl,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        caption: bot.description || `${bot.name} has a new update.`,
        viewed: false,
        botId: bot.id,
        isOwn: false,
        isSynthetic: true,
      } satisfies StoryRailItem;
    });

    return syntheticStories;
  }, [botStories, bots]);

  const openViewer = (storyId: string) => {
    setViewerStoryId(storyId);
    setIsViewerOpen(true);
  };

  return (
    <>
      <div className="overflow-x-auto scrollbar-hide border-b border-[color:var(--jr-border)] bg-[color:rgba(29,18,17,0.84)] px-4 py-4">
        <div className="flex min-w-max items-start gap-4">
          {displayStories.map((story) => (
            <StoryBadge
              key={story.id}
              label={story.authorName}
              imageSrc={story.thumbnailUrl}
              fallbackText={getStoryFallback(story.authorName)}
              borderClassName={getBorderClass(story)}
              isVideo={story.mediaType === 'video'}
              onClick={() => openViewer(story.id)}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="px-4 pb-2 text-center text-sm text-[color:#efc2bd]">{error}</div>
      )}
      {!error && (isLoading || botsLoading) && (
        <div className="px-4 pb-2 text-center text-sm text-[color:var(--jr-text-muted)]">
          Loading stories...
        </div>
      )}

      <StoryViewer
        open={isViewerOpen}
        stories={displayStories}
        initialStoryId={viewerStoryId}
        onOpenChange={(nextOpen) => {
          setIsViewerOpen(nextOpen);
          if (!nextOpen) {
            void refetch();
          }
        }}
        onRefresh={refetch}
      />
    </>
  );
}
